import { ServerError, LazyQueryExecFunction, ApolloError } from '@apollo/client';
import { useCallback, useMemo } from 'react';
import usePolling from './usePolling';
import { getApolloErrorMessage } from '../helpers/remapApolloErrors';
import { differenceInSeconds } from 'date-fns';

const timeLimit = 60; // @TODO: adjust time for production use

export type ServerErrorType = ServerError & {
  result: { errors: Array<{ extensions: { oldestCalculationStartTimestamp: number } }> };
};

export type ReturnType<T> = {
  data: T | undefined;
  error: ApolloError | null;
};

export type UseQueryPollingProps<T, V> = {
  queryFn: LazyQueryExecFunction<T, V>;
  options: V & { filterKey?: string };
  // options: V;
};

const getStartingCalculationTime = (error: ServerErrorType): number => {
  const extensions = error.result.errors[0].extensions;
  return extensions.oldestCalculationStartTimestamp * 1000;
};

const useFetchQueueWithPolling = () => {
  const { startPolling, stopPolling, isPolling } = usePolling();

  const startQuery = useCallback(
    <T, V>(props: UseQueryPollingProps<T, V>): Promise<ReturnType<T>> => {
      const { queryFn, options } = props;

      if (isPolling) {
        stopPolling();
      }

      const timeOutError: Error = {
        name: 'POST_CALCULATION_TIMEOUT',
        message: 'POST_CALCULATION_TIMEOUT'
      };

      return new Promise((resolve, reject) => {
        queryFn({ variables: options })
          .then(results => {
            const { data, error } = results;

            if (error && getApolloErrorMessage(error) === 'QUEUE_STILL_CALCULATING') {
              throw error;
            }

            resolve({ data, error: null });
          })
          .catch((error: ApolloError) => {
            const serverError = error.networkError as ServerErrorType;
            const calculationTime = getStartingCalculationTime(serverError);

            if (differenceInSeconds(new Date(), calculationTime) > timeLimit) {
              reject(new ApolloError({ networkError: timeOutError }));
              return;
            }

            startPolling(() => {
              queryFn({ variables: options })
                .then(results => {
                  const { data, error } = results;

                  if (error && getApolloErrorMessage(error) === 'QUEUE_STILL_CALCULATING') {
                    throw error;
                  }

                  stopPolling();
                  resolve({ data, error: null });
                })
                .catch((error: ApolloError) => {
                  const serverError = error.networkError as ServerErrorType;
                  const calculationTime = getStartingCalculationTime(serverError);

                  if (differenceInSeconds(new Date(), calculationTime) > timeLimit) {
                    stopPolling();
                    reject(new ApolloError({ networkError: timeOutError }));
                  }
                });
            }, 3000);
          });
      });
    },
    [isPolling, startPolling, stopPolling]
  );

  // memoize to prevent new object when re-rending
  return useMemo(() => {
    return {
      startQuery
    };
  }, [startQuery]);
};

export default useFetchQueueWithPolling;
